import {
  Button,
  IconButton,
  useToast,
  ButtonProps,
  Tooltip,
} from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useState } from 'react';

import {
  useGetCurrentDeliverableSubmissionDownloadUrlLazyQueryGQLType,
  useGetDeliverableSubmissionDownloadUrlLazyQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  errorToastId,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { FiDownload } from 'react-icons/fi';

export const useCurrentDeliverableFileSubmissionDownloadUrl = (
  deliverableId: string,
  fileName: string,
) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [
    _getDownloadUrl,
    { loading: urlLoading, error },
  ] = useGetCurrentDeliverableSubmissionDownloadUrlLazyQueryGQLType({
    variables: { deliverableId },
    onError: (err) => {
      toast({ ...errorToastConfig, title: err.message });
    },
    onCompleted: (data) => {
      if (
        data &&
        data?.deliverable?.currentSubmission?.__typename ===
          'DeliverableFileSubmission'
      ) {
        const { submissionDownloadUrl } = data.deliverable.currentSubmission;
        axios({
          url: submissionDownloadUrl,
          method: 'GET',
          responseType: 'blob',
        })
          .then((response) => {
            const fileBlob = new Blob([response.data], {
              type: response.data.type,
            });
            FileSaver.saveAs(fileBlob, fileName);
            setLoading(false);
          })
          .catch((err) => {
            if (
              !toast.isActive(`${errorToastId}-deliverable-submission-download`)
            ) {
              errorToastConfig.title = `Error downloading submission ${fileName}`;
              toast(errorToastConfig);
            }
            throw err;
          });
      }
    },
  });

  return {
    getDownloadUrl: () => {
      setLoading(true);
      _getDownloadUrl();
    },
    loading: loading || urlLoading,
    error,
  };
};

export const useDeliverableFileSubmissionDownloadUrl = (
  deliverableId: string,
  deliverableSubmissionId: string,
  fileName: string,
) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [
    _getDownloadUrl,
    { loading: urlLoading, error },
  ] = useGetDeliverableSubmissionDownloadUrlLazyQueryGQLType({
    variables: { deliverableId, deliverableSubmissionId },
    onError: (err) => {
      toast({ ...errorToastConfig, title: err.message });
    },
    onCompleted: (data) => {
      if (
        data &&
        data?.deliverable?.submissions[0]?.__typename ===
          'DeliverableFileSubmission'
      ) {
        const { submissionDownloadUrl } = data.deliverable.submissions[0];
        axios({
          url: submissionDownloadUrl,
          method: 'GET',
          responseType: 'blob',
        })
          .then((response) => {
            const fileBlob = new Blob([response.data], {
              type: response.data.type,
            });
            FileSaver.saveAs(fileBlob, fileName);
            setLoading(false);
          })
          .catch((err) => {
            if (
              !toast.isActive(`${errorToastId}-deliverable-submission-download`)
            ) {
              errorToastConfig.title = `Error downloading submission ${fileName}`;
              toast(errorToastConfig);
            }
            throw err;
          });
      }
    },
  });

  return {
    getDownloadUrl: () => {
      setLoading(true);
      _getDownloadUrl();
    },
    loading: loading || urlLoading,
    error,
  };
};

type DownloadCurrentDeliverableSubmissionButtonProps = {
  deliverableId: string;
  fileName: string;
  children?: React.ReactElement;
  'aria-label'?: string;
  colorScheme?: ButtonProps['colorScheme'];
};
export const DownloadCurrentDeliverableSubmissionButton = ({
  deliverableId,
  fileName,
  children,
  ...buttonProps
}: DownloadCurrentDeliverableSubmissionButtonProps) => {
  const {
    getDownloadUrl,
    loading,
  } = useCurrentDeliverableFileSubmissionDownloadUrl(deliverableId, fileName);

  return (
    <Button
      {...(buttonProps || [])}
      leftIcon={<FiDownload />}
      onClick={getDownloadUrl}
      isLoading={loading}
    >
      {children || 'Download Current Submission'}
    </Button>
  );
};

type DownloadCurrentDeliverableSubmissionIconButtonProps = {
  deliverableId: string;
  fileName: string;
  'aria-label'?: string;
  label?: string;
};
export const DownloadCurrentDeliverableSubmissionIconButton = ({
  deliverableId,
  fileName,
  label,
  ...otherProps
}: DownloadCurrentDeliverableSubmissionIconButtonProps) => {
  const {
    getDownloadUrl,
    loading,
  } = useCurrentDeliverableFileSubmissionDownloadUrl(deliverableId, fileName);

  return (
    <Tooltip label={label || 'Download Current Submission'}>
      <IconButton
        aria-label={otherProps['aria-label'] || 'Download Current Submission'}
        onClick={getDownloadUrl}
        isLoading={loading}
        icon={<FiDownload />}
        variant="ghost"
      />
    </Tooltip>
  );
};

type DownloadDeliverableSubmissionIconButtonProps = {
  deliverableId: string;
  deliverableSubmissionId: string;
  fileName: string;
  'aria-label'?: string;
  label?: string;
};
export const DownloadDeliverableSubmissionIconButton = ({
  deliverableId,
  deliverableSubmissionId,
  fileName,
  label,
  ...otherProps
}: DownloadDeliverableSubmissionIconButtonProps) => {
  const { getDownloadUrl, loading } = useDeliverableFileSubmissionDownloadUrl(
    deliverableId,
    deliverableSubmissionId,
    fileName,
  );

  return (
    <Tooltip label={label || 'Download Current Submission'}>
      <IconButton
        aria-label={otherProps['aria-label'] || 'Download Submission'}
        onClick={getDownloadUrl}
        isLoading={loading}
        icon={<FiDownload />}
        variant="ghost"
      />
    </Tooltip>
  );
};
